import React, { useState, useEffect } from "react";
import { getOrFetchToken } from "../../hooks/useViewerToken";

const BASE_URL_DASH = "https://mis.empowerplus.in";

/* ── FILTER DEFINITIONS ── */
const FILTER_DEFS = [
    { key: "fy", label: "FY", type: "multi", apiKey: "fy" },
    { key: "date", label: "Date", type: "daterange" },
    { key: "fundingSource", label: "Funding Source", type: "multi", apiKey: "fundingSource" },
    { key: "scheme", label: "Scheme", type: "hierarchical", apiKey: "schemes" },
    { key: "subScheme", label: "Sub Scheme", type: "hierarchical", apiKey: "subSchemes" },
    { key: "project", label: "Project", type: "hierarchical", apiKey: "projects" },
    { key: "phase", label: "Phase", type: "multi", apiKey: "phases" },
    { key: "state", label: "State", type: "hierarchical", apiKey: "states" },
    { key: "district", label: "District", type: "hierarchical", apiKey: "districts" },
    { key: "centre", label: "Centre", type: "hierarchical", apiKey: "centers" },
    { key: "funder", label: "Funder", type: "multi", apiKey: "funders" },
    { key: "gender", label: "Gender", type: "multi", staticOptions: ["Male", "Female", "Transgender"] },
    { key: "sector", label: "Sector", type: "multi", apiKey: "sectors" },
];

const FLT_DEFAULT_OPEN = { fy: true, subScheme: true, state: true };
const EMPTY_FILTERS = { fy: [], fundingSource: [], scheme: [], subScheme: [], project: [], phase: [], state: [], district: [], centre: [], funder: [], gender: [], sector: [] };

/* ── FILTER SIDEBAR ── */
function FilterGroup({ filter, open, onToggle, options, selected, onToggleValue }) {
    return (
        <div className="flt-group">
            <button className="flt-group-hd" onClick={onToggle}>
                <span className="flt-group-lbl">{filter.label}</span>
                <span className={`flt-chevron${open ? " open" : ""}`}>›</span>
            </button>
            {open && (
                <div className="flt-body">
                    {filter.type === "daterange" && (
                        <div className="flt-daterange">
                            <label>Start</label>
                            <input type="date" className="flt-date-input" />
                            <label>End</label>
                            <input type="date" className="flt-date-input" />
                        </div>
                    )}
                    {(filter.type === "multi" || filter.type === "hierarchical") && options.map(opt => (
                        <label key={opt} className="flt-check-row">
                            <input
                                type="checkbox"
                                className="flt-cb"
                                checked={selected.includes(opt)}
                                onChange={() => onToggleValue(filter.key, opt)}
                            />
                            <span>{opt}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function FilterSidebar({ selected, onToggleValue, onApply, onReset, apiOptions }) {
    const [openFilters, setOpenFilters] = useState(FLT_DEFAULT_OPEN);

    const getOptions = (filter) => {
        if (filter.staticOptions) return filter.staticOptions;
        if (apiOptions && filter.apiKey && Array.isArray(apiOptions[filter.apiKey]))
            return apiOptions[filter.apiKey];
        return [];
    };

    const toggle = (key) => setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
    const totalSelected = Object.values(selected).flat().length;

    return (
        <aside className="flt-sidebar">
            <div className="flt-header">
                <span className="flt-title">Filters {totalSelected > 0 && <span className="flt-badge">{totalSelected}</span>}</span>
                <button className="flt-reset" onClick={onReset}>Reset All</button>
            </div>
            <div className="flt-note">All filter views change according to selection.</div>
            {FILTER_DEFS.map(filter => (
                <FilterGroup
                    key={filter.key}
                    filter={filter}
                    open={!!openFilters[filter.key]}
                    onToggle={() => toggle(filter.key)}
                    options={getOptions(filter)}
                    selected={selected[filter.key] || []}
                    onToggleValue={onToggleValue}
                />
            ))}
            <button className="flt-apply" onClick={onApply}>Apply Filters</button>
            <style>{`
.flt-sidebar { width:256px; flex-shrink:0; background:#fff; border-left:1px solid #e5e7eb; padding:16px 14px 24px; min-height:100vh; position:sticky; top:0; max-height:100vh; overflow-y:auto; font-family:Inter,system-ui,sans-serif; }
.flt-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.flt-title { font-size:14px; font-weight:900; color:#0f172a; display:flex; align-items:center; gap:6px; }
.flt-badge { background:#2563eb; color:#fff; font-size:10px; font-weight:800; border-radius:999px; padding:1px 6px; }
.flt-reset { font-size:11px; font-weight:700; color:#6b7280; background:none; border:1px solid #e5e7eb; border-radius:6px; padding:3px 8px; cursor:pointer; }
.flt-reset:hover { color:#ef4444; border-color:#fca5a5; }
.flt-note { font-size:10px; color:#9ca3af; line-height:1.4; background:#f8fafc; border-radius:6px; padding:6px 8px; margin-bottom:12px; }
.flt-group { border-bottom:1px solid #f1f5f9; }
.flt-group-hd { display:flex; align-items:center; justify-content:space-between; width:100%; background:none; border:none; padding:9px 2px; cursor:pointer; font-size:12px; font-weight:800; color:#374151; text-align:left; }
.flt-group-hd:hover { color:#2563eb; }
.flt-group-lbl { flex:1; }
.flt-chevron { font-size:14px; color:#9ca3af; transform:rotate(90deg); display:inline-block; transition:transform 0.2s; line-height:1; }
.flt-chevron.open { transform:rotate(270deg); }
.flt-body { padding:4px 0 10px 4px; display:flex; flex-direction:column; gap:4px; max-height:180px; overflow-y:auto; }
.flt-check-row { display:flex; align-items:center; gap:7px; font-size:12px; color:#4b5563; cursor:pointer; padding:2px 4px; border-radius:5px; }
.flt-check-row:hover { background:#eff6ff; color:#2563eb; }
.flt-cb { accent-color:#2563eb; width:13px; height:13px; flex-shrink:0; cursor:pointer; }
.flt-daterange { display:flex; flex-direction:column; gap:5px; font-size:11px; font-weight:700; color:#6b7280; }
.flt-date-input { border:1px solid #e5e7eb; border-radius:6px; padding:5px 7px; font-size:11px; color:#374151; outline:none; }
.flt-date-input:focus { border-color:#2563eb; }
.flt-apply { width:100%; margin-top:16px; background:#2563eb; color:#fff; border:none; border-radius:10px; padding:10px; font-size:13px; font-weight:800; cursor:pointer; }
.flt-apply:hover { background:#1d4ed8; }
            `}</style>
        </aside>
    );
}

/* ─── STATIC DATA ─── */

const DEMOGRAPHICS = [
    { label: "Age Category (Majority)",  value: "25–40 yrs",    sub: "68% of enrolled women",                     color: "#d946ef" },
    { label: "Education Level",          value: "Secondary+",   sub: "74% completed secondary schooling",          color: "#7c3aed" },
    { label: "Marital Status",           value: "72% Married",  sub: "Single 18% · Widowed/Separated 10%",         color: "#0e7490" },
];

const BUSINESS_FORMATION_CARDS = [
    { label: "Businesses Started",            value: "4,280",  sub: "New enterprises launched",               color: "#d946ef" },
    { label: "Business Survival Rate (1 yr)", value: "78.4%",  sub: "Still operational at 12 months",         color: "#059669" },
    { label: "Enterprises Formally Registered",value: "2,640", sub: "With Udyam / GST / FSSAI",               color: "#7c3aed" },
    { label: "Women in Group Enterprises",    value: "1,840",  sub: "SHG or collective business models",       color: "#f59e0b" },
];

const BUSINESS_TYPE_DATA = [
    { name: "Food & Agri",       count: 1240 },
    { name: "Tailoring / Crafts",count: 980  },
    { name: "Retail / Trade",    count: 860  },
    { name: "Beauty & Wellness", count: 640  },
    { name: "Dairy / Livestock", count: 420  },
    { name: "Digital Services",  count: 140  },
];

const FINANCIAL_CARDS = [
    { label: "Women Linked to Credit / Loans",   value: "3,120", sub: "Formal credit access established",      color: "#d946ef" },
    { label: "Average Loan Amount Accessed",      value: "₹48K", sub: "Per beneficiary average",               color: "#7c3aed" },
    { label: "Linked to Govt Schemes",            value: "56%",   sub: "PMEGP · Mudra · SHG-BLP",              color: "#059669" },
    { label: "Savings Groups Formed",             value: "312",   sub: "SHGs and collective saving circles",    color: "#0e7490" },
];

const SAVINGS_GROUP_DATA = [
    { name: "Maharashtra",    count: 84  },
    { name: "Gujarat",        count: 62  },
    { name: "Rajasthan",      count: 54  },
    { name: "Uttar Pradesh",  count: 48  },
    { name: "Karnataka",      count: 38  },
    { name: "Others",         count: 26  },
];

const MARKET_CARDS = [
    { label: "Women with Active Market Linkages", value: "2,840",    sub: "Linked to buyers / aggregators",     color: "#d946ef" },
    { label: "Average Monthly Revenue",           value: "₹18,400",  sub: "Per active enterprise",              color: "#7c3aed" },
    { label: "Total Revenue Generated (Annual)",  value: "₹62 Cr",   sub: "Aggregate across all enterprises",   color: "#059669" },
];

const REVENUE_SECTOR_DATA = [
    { name: "Food & Agri",        count: 1840 },
    { name: "Tailoring / Crafts", count: 1260 },
    { name: "Retail / Trade",     count: 1080 },
    { name: "Beauty & Wellness",  count: 780  },
    { name: "Dairy / Livestock",  count: 540  },
    { name: "Digital Services",   count: 300  },
];

const CAPACITY_CARDS = [
    { label: "Women Trained in EDP",          value: "8,900",  sub: "Entrepreneurship Dev Programme",    color: "#d946ef" },
    { label: "Women Certified",               value: "7,340",  sub: "82.5% certification rate",          color: "#059669" },
    { label: "Mentorship Sessions Conducted", value: "1,240",  sub: "1-on-1 and group sessions",         color: "#7c3aed" },
    { label: "Mentors Engaged",               value: "148",    sub: "Active mentors across all centres",  color: "#0e7490" },
];

const SOCIAL_CARDS = [
    { label: "Reporting Household Income Increase",    value: "71%", sub: "6-month follow-up survey",           color: "#d946ef" },
    { label: "Reporting Improved Decision-Making",     value: "64%", sub: "Financial & household decisions",    color: "#7c3aed" },
    { label: "Women in Community Leadership Roles",    value: "28%", sub: "Ward committee · SHG president etc", color: "#059669" },
];

const PROGRAM_EXP = [
    { label: "Training Satisfaction Score", value: "4.4 / 5", sub: "Avg rating from exit surveys", color: "#d946ef" },
    { label: "Recommendation Score",         value: "86%",     sub: "Would recommend to peers",     color: "#059669" },
];

/* ─── HELPERS ─── */

function SectionBadge({ n, label }) {
    return (
        <div className="we-section-hd">
            <span className="we-section-badge">Section {n}</span>
            <span className="we-section-title">{label}</span>
        </div>
    );
}

function StatCard({ label, value, sub, color }) {
    return (
        <div className="we-card we-stat" style={{ borderTop: `4px solid ${color}` }}>
            <div className="we-stat-val" style={{ color }}>{value}</div>
            <div className="we-stat-lbl">{label}</div>
            {sub && <div className="we-stat-sub">{sub}</div>}
        </div>
    );
}

function VerticalBarChart({ data, nameKey = "name", valueKey = "count", height = 180, color1, color2 }) {
    const max = Math.max(...data.map(d => d[valueKey]));
    return (
        <div className="we-vbar-wrap" style={{ height }}>
            {data.map(d => (
                <div key={d[nameKey]} className="we-vbar-item">
                    <div className="we-vbar-val">{d[valueKey].toLocaleString()}</div>
                    <div className="we-vbar-track">
                        <div
                            className="we-vbar-fill"
                            style={{
                                height: `${(d[valueKey] / max) * 100}%`,
                                background: `linear-gradient(to top, ${color1}, ${color2})`,
                            }}
                        />
                    </div>
                    <div className="we-vbar-name">{d[nameKey]}</div>
                </div>
            ))}
        </div>
    );
}

/* ─── PAGE ─── */

export default function WomenEntrepreneurshipPage() {

    const [selected, setSelected] = useState(EMPTY_FILTERS);
    const [apiOptions, setApiOptions] = useState(null);

    useEffect(() => {
        (async () => {
            const token = await getOrFetchToken();
            if (!token) return;
            fetch(`${BASE_URL_DASH}/api/view-dashboard/filter-options`, {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data) setApiOptions(data); })
                .catch(() => {});
        })();
    }, []);

    const handleToggleValue = (filterKey, value) => {
        setSelected(prev => {
            const current = prev[filterKey] || [];
            return {
                ...prev,
                [filterKey]: current.includes(value)
                    ? current.filter(v => v !== value)
                    : [...current, value],
            };
        });
    };

    const handleReset = () => setSelected(EMPTY_FILTERS);

    return (
        <div className="we-wrapper">

            {/* ── MAIN ── */}
            <div className="we-main">

                {/* Page Header */}
                <div className="we-page-header">
                    <div>
                        <div className="we-breadcrumb">Impact Dimensions</div>
                        <h1 className="we-title">Women Entrepreneurship Metrics</h1>
                        <p className="we-subtitle">Business formation, financial access, market linkages, and social outcomes for women entrepreneurs</p>
                    </div>
                    <div className="we-badge">Static · FY 2024–25</div>
                </div>

                {/* ── SECTION 1: Demographics ── */}
                <SectionBadge n={1} label="Demographics" />
                <div className="we-cards-3">
                    {DEMOGRAPHICS.map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* ── SECTION 2: Business Formation ── */}
                <SectionBadge n={2} label="Business Formation" />
                <div className="we-cards-4" style={{ marginBottom: 12 }}>
                    {BUSINESS_FORMATION_CARDS.map(d => <StatCard key={d.label} {...d} />)}
                </div>
                <div className="we-card we-chart-card">
                    <div className="we-chart-title">Enterprises by Business Type</div>
                    <VerticalBarChart
                        data={BUSINESS_TYPE_DATA}
                        height={185}
                        color1="#86198f"
                        color2="#e879f9"
                    />
                </div>

                {/* ── SECTION 3: Financial Access & Linkages ── */}
                <SectionBadge n={3} label="Financial Access & Linkages" />
                <div className="we-cards-4" style={{ marginBottom: 12 }}>
                    {FINANCIAL_CARDS.map(d => <StatCard key={d.label} {...d} />)}
                </div>
                <div className="we-card we-chart-card">
                    <div className="we-chart-title">Savings Groups (SHGs) Formed by State</div>
                    <VerticalBarChart
                        data={SAVINGS_GROUP_DATA}
                        height={170}
                        color1="#0e7490"
                        color2="#67e8f9"
                    />
                </div>

                {/* ── SECTION 4: Market Linkages & Revenue ── */}
                <SectionBadge n={4} label="Market Linkages & Revenue" />
                <div className="we-cards-3" style={{ marginBottom: 12 }}>
                    {MARKET_CARDS.map(d => <StatCard key={d.label} {...d} />)}
                </div>
                <div className="we-card we-chart-card">
                    <div className="we-chart-title">Revenue by Business Sector (₹ in Lakhs)</div>
                    <VerticalBarChart
                        data={REVENUE_SECTOR_DATA}
                        height={175}
                        color1="#7c3aed"
                        color2="#c4b5fd"
                    />
                </div>

                {/* ── SECTION 5: Capacity Building ── */}
                <SectionBadge n={5} label="Capacity Building" />
                <div className="we-cards-4">
                    {CAPACITY_CARDS.map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* ── SECTION 6: Social & Household Outcomes ── */}
                <SectionBadge n={6} label="Social & Household Outcomes" />
                <div className="we-cards-3">
                    {SOCIAL_CARDS.map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* ── SECTION 7: Program Experience ── */}
                <SectionBadge n={7} label="Program Experience" />
                <div className="we-cards-2">
                    {PROGRAM_EXP.map(d => <StatCard key={d.label} {...d} />)}
                </div>

            </div>

            {/* ── FILTER SIDEBAR ── */}
            <FilterSidebar
                selected={selected}
                onToggleValue={handleToggleValue}
                onApply={() => {}}
                onReset={handleReset}
                apiOptions={apiOptions}
            />

            <style>{`
.we-wrapper {
    display: flex;
    align-items: flex-start;
    background: #f8fafc;
    min-height: 100vh;
    font-family: Inter, system-ui, sans-serif;
    color: #111827;
}
.we-main { flex: 1; min-width: 0; padding: 24px 28px; }

.we-page-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 20px;
}
.we-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
.we-title      { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
.we-subtitle   { font-size: 13px; color: #64748b; }
.we-badge {
    background: #fdf4ff; color: #86198f;
    font-size: 11px; font-weight: 800; padding: 5px 12px;
    border-radius: 999px; white-space: nowrap;
}

.we-section-hd {
    display: flex; align-items: center; gap: 10px; margin: 22px 0 10px;
}
.we-section-badge {
    background: #86198f; color: #fff;
    font-size: 10px; font-weight: 900; padding: 3px 9px;
    border-radius: 999px; white-space: nowrap; letter-spacing: 0.4px;
}
.we-section-title { font-size: 14px; font-weight: 900; color: #0f172a; }

.we-cards-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.we-cards-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.we-cards-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }

.we-card {
    background: #fff; border: 1px solid #e5e7eb;
    border-radius: 12px; padding: 16px 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.we-stat-val { font-size: 24px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
.we-stat-lbl { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
.we-stat-sub { font-size: 11px; color: #9ca3af; line-height: 1.3; }

.we-chart-card  { padding: 18px 20px; }
.we-chart-title { font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 14px; }

.we-vbar-wrap {
    display: flex; gap: 10px; align-items: flex-end; padding-bottom: 8px;
}
.we-vbar-item {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; gap: 4px; height: 100%;
}
.we-vbar-val  { font-size: 10px; font-weight: 800; color: #374151; }
.we-vbar-track {
    flex: 1; width: 100%; max-width: 60px;
    background: #f1f5f9; border-radius: 6px 6px 0 0;
    display: flex; align-items: flex-end; overflow: hidden;
}
.we-vbar-fill { width: 100%; border-radius: 6px 6px 0 0; transition: height 0.4s ease; }
.we-vbar-name {
    font-size: 10px; font-weight: 700; color: #6b7280;
    text-align: center; line-height: 1.3; max-width: 72px;
}
            `}</style>
        </div>
    );
}
